import { useState, useEffect, useMemo } from "react";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { RefreshCw, Package, Store, Upload, Calendar, Search, Image, X, ChevronLeft, ChevronRight, Plus, Trash2, DollarSign, Settings, CheckCircle } from "lucide-react";

const CURRENCY_MAP = { TRY: "₺", USD: "$", EUR: "€", GBP: "£" };
const fmtPrice = (amount, code) => {
  const sym = CURRENCY_MAP[(code || "TRY").toUpperCase()] || code || "₺";
  return `${parseFloat(amount || 0).toLocaleString("tr-TR")} ${sym}`;
};

const API = "/api";

// SessionStorage key for product state persistence
const PRODUCTS_STORAGE_KEY = "nebim_products_cache";

export default function ProductsPage() {
  const { token } = useAuth();
  const [nebimProducts, setNebimProducts] = useState(() => {
    // Load from sessionStorage on initial mount
    try {
      const cached = sessionStorage.getItem(PRODUCTS_STORAGE_KEY);
      if (cached) {
        const parsed = JSON.parse(cached);
        return parsed.products || [];
      }
    } catch (e) {
      console.error("Failed to load from sessionStorage:", e);
    }
    return [];
  });
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  
  // Sync parameters - Load from sessionStorage
  const [syncDate, setSyncDate] = useState(() => {
    try {
      const cached = sessionStorage.getItem(PRODUCTS_STORAGE_KEY);
      if (cached) {
        const parsed = JSON.parse(cached);
        return parsed.syncDate || "2024-01-01";
      }
    } catch (e) {}
    return "2024-01-01";
  });
  const [itemCodes, setItemCodes] = useState("");
  
  // Search/filter
  const [searchTerm, setSearchTerm] = useState("");
  
  // Selection state
  const [selectedProductKeys, setSelectedProductKeys] = useState(new Set());
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;
  
  // Photo modal
  const [photoModalOpen, setPhotoModalOpen] = useState(false);
  const [photoModalProduct, setPhotoModalProduct] = useState(null);
  const [productPhotos, setProductPhotos] = useState([]);
  const [loadingPhotos, setLoadingPhotos] = useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  
  // Shopify export
  const [exporting, setExporting] = useState(false);
  
  // Miktar bazlı fiyatlandırma (Quantity Pricing)
  const [pricingModalOpen, setPricingModalOpen] = useState(false);
  const [pricingProduct, setPricingProduct] = useState(null);
  const [quantityPricing, setQuantityPricing] = useState([]);
  const [productQuantityPricing, setProductQuantityPricing] = useState({});  // key -> [{quantity, totalPrice}]
  
  // Save products and syncDate to sessionStorage when they change
  useEffect(() => {
    if (nebimProducts.length > 0) {
      try {
        sessionStorage.setItem(PRODUCTS_STORAGE_KEY, JSON.stringify({
          products: nebimProducts,
          syncDate: syncDate,
          savedAt: new Date().toISOString()
        }));
      } catch (e) {
        console.error("Failed to save to sessionStorage:", e);
      }
    }
  }, [nebimProducts, syncDate]);

  // Ürünleri ProductCode + ColorCode bazında grupla (TÜM bedenler tek üründe)
  const groupedProducts = useMemo(() => {
    if (!nebimProducts.length) return [];
    const grouped = {};
    nebimProducts.forEach(p => {
      const key = `${p.ProductCode}-${p.ColorCode || ""}`;
      if (!grouped[key]) {
        grouped[key] = {
          key,
          ProductCode: p.ProductCode,
          ProductName: p.ProductName,
          ColorCode: p.ColorCode,
          ColorDescription: p.ColorDescription,
          Brand: p.Brand,
          Photo: p.Photo,
          SalesPrice: p.SalesPrice,
          SalesPriceUSD: p.SalesPriceUSD,
          SalesPriceEUR: p.SalesPriceEUR,
          SalesPriceGBP: p.SalesPriceGBP,
          ListPrice: p.ListPrice,
          BuyPrice: p.BuyPrice,
          CurrencyCode: p.CurrencyCode || "TRY",
          Notes: p.Notes,
          variants: [],
          totalStock: 0
        };
      }
      // Her beden bir variant olarak ekleniyor
      grouped[key].variants.push({
        size: p.ItemDim1Code || "STD",
        sku: p.SKU,
        barcode: p.Barcode,
        stock: p.StockQuantity || 0,
        price: p.SalesPrice || 0
      });
      grouped[key].totalStock += p.StockQuantity || 0;
    });
    return Object.values(grouped);
  }, [nebimProducts]);

  // Filtreleme
  const filteredProducts = useMemo(() => {
    if (!searchTerm) return groupedProducts;
    const term = searchTerm.toLowerCase();
    return groupedProducts.filter(p =>
      p.ProductCode?.toLowerCase().includes(term) ||
      p.ProductName?.toLowerCase().includes(term) ||
      p.ColorDescription?.toLowerCase().includes(term)
    );
  }, [groupedProducts, searchTerm]);

  // Sayfalama
  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
  const paginatedProducts = filteredProducts.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Selection handlers
  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedProductKeys(new Set(filteredProducts.map(p => p.key)));
    } else {
      setSelectedProductKeys(new Set());
    }
  };

  const handleSelectProduct = (key, checked) => {
    setSelectedProductKeys(prev => {
      const newSet = new Set(prev);
      if (checked) newSet.add(key);
      else newSet.delete(key);
      return newSet;
    });
  };

  const isAllSelected = filteredProducts.length > 0 && selectedProductKeys.size === filteredProducts.length;

  // Ürünleri getir
  const fetchNebimProducts = async () => {
    setLoading(true);
    // Eski verileri SADECE başarılı response durumunda temizle
    try {
      // Ürün kodu varsa tarih 1900, yoksa seçilen tarih
      const dateToSend = (itemCodes && itemCodes.trim()) ? "1900-01-01T00:00:00.000" : `${syncDate}T00:00:00.000`;
      let url = `${API}/nebim/products?token=${token}&date=${dateToSend}`;
      // Ürün kodları varsa ekle
      if (itemCodes && itemCodes.trim()) {
        url += `&itemCodes=${encodeURIComponent(itemCodes.trim())}`;
      }
      console.log("API çağrısı:", url); // Debug
      const response = await axios.get(url);
      console.log("API yanıtı:", response.data.total || response.data.data?.length, "ürün"); // Debug
      if (response.data.success) {
        const newProducts = response.data.data || [];
        setNebimProducts(newProducts);
        toast.success(`${newProducts.length} ürün getirildi`);
      } else {
        toast.error(response.data.error || "Ürünler alınamadı");
      }
    } catch (error) {
      console.error("Ürün çekme hatası:", error);
      toast.error("Ürün çekme hatası");
    } finally {
      setLoading(false);
    }
  };

  // Ürünleri senkronize et
  const syncProducts = async () => {
    setSyncing(true);
    try {
      const itemCodeList = itemCodes ? itemCodes.split(",").map(c => c.trim()).filter(Boolean) : null;
      // Ürün kodu varsa tarih 1900, yoksa seçilen tarih
      const dateToSend = (itemCodeList && itemCodeList.length > 0) ? "1900-01-01" : syncDate;
      const response = await axios.post(`${API}/nebim/sync-products?token=${token}&date=${dateToSend}`, itemCodeList);
      
      if (response.data.success || response.data.ok) {
        const products = response.data.data || [];
        const count = products.length || response.data.insertedOrUpdated || 0;
        toast.success(`${count} ürün senkronize edildi`);
        
        // Ürünleri doğrudan state'e kaydet (fetchNebimProducts'a gerek yok)
        setNebimProducts(products);
      } else {
        toast.error(response.data.error || "Senkronizasyon başarısız");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Senkronizasyon hatası");
    } finally {
      setSyncing(false);
    }
  };

  // Fotoğraf modal'ını aç
  const openPhotoModal = async (product) => {
    setPhotoModalProduct(product);
    setPhotoModalOpen(true);
    setLoadingPhotos(true);
    setCurrentPhotoIndex(0);
    setProductPhotos([]);

    try {
      const response = await axios.get(
        `${API}/nebim/product-photos?token=${token}&item_code=${product.ProductCode}&color_code=${product.ColorCode}`
      );
      if (response.data.success && response.data.photos?.length > 0) {
        setProductPhotos(response.data.photos);
      } else {
        // Fallback: Ana fotoğrafı kullan
        if (product.Photo) {
          setProductPhotos([{ Photo: product.Photo, Sira: 1 }]);
        }
      }
    } catch (error) {
      console.error("Fotoğraf çekme hatası:", error);
      if (product.Photo) {
        setProductPhotos([{ Photo: product.Photo, Sira: 1 }]);
      }
    } finally {
      setLoadingPhotos(false);
    }
  };

  // Shopify'a aktar - TÜM fotoğraflar + Kategori + Notes + Miktar Bazlı Fiyatlandırma
  const exportToShopify = async () => {
    if (selectedProductKeys.size === 0) {
      toast.error("Lütfen aktarılacak ürünleri seçin");
      return;
    }

    setExporting(true);
    try {
      const selectedProducts = groupedProducts.filter(p => selectedProductKeys.has(p.key));
      
      toast.info(`${selectedProducts.length} ürün hazırlanıyor...`);
      
      // Her ürün için fotoğrafları ve kategorileri çek
      const productsWithData = [];
      for (const product of selectedProducts) {
        // Fotoğrafları çek
        let photos = [];
        try {
          const photoResponse = await axios.get(
            `${API}/nebim/product-photos?token=${token}&item_code=${product.ProductCode}&color_code=${product.ColorCode}`
          );
          if (photoResponse.data.success && photoResponse.data.photos?.length > 0) {
            photos = photoResponse.data.photos
              .sort((a, b) => (a.Sira || 0) - (b.Sira || 0))
              .map(p => p.Photo);
          }
        } catch (e) {
          console.error(`Fotoğraf hatası (${product.ProductCode}):`, e);
        }
        
        // Kategori çek - TÜM kategorileri al
        let categories = [];
        try {
          const categoryResponse = await axios.get(
            `${API}/nebim/product-hierarchy?token=${token}&item_code=${product.ProductCode}`
          );
          if (categoryResponse.data.success && categoryResponse.data.hierarchy?.length > 0) {
            // TÜM kategorileri al
            categories = categoryResponse.data.hierarchy.map(h => h.ProductHierarchyLevel);
          }
        } catch (e) {
          console.error(`Kategori hatası (${product.ProductCode}):`, e);
        }
        
        // Miktar bazlı fiyatlandırma verisi
        const qPricing = productQuantityPricing[product.key] || [];
        
        productsWithData.push({
          ...product,
          images: photos,
          categories: categories, // TÜM kategorileri gönder
          description: product.Notes || "", // Notes'u description olarak gönder
          quantityPricing: qPricing // Miktar bazlı fiyatlandırma
        });
      }

      toast.info("Shopify'a aktarılıyor...");

      const response = await axios.post(`${API}/nebim/export-to-shopify?token=${token}`, {
        products: productsWithData
      });

      if (response.data.success) {
        toast.success(`${response.data.exported || selectedProducts.length} ürün Shopify'a aktarıldı`);
        setSelectedProductKeys(new Set());
      } else {
        toast.error(response.data.error || "Aktarım başarısız");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Aktarım hatası");
    } finally {
      setExporting(false);
    }
  };

  // Sadece stokları güncelle (Shopify'da mevcut ürünler için)
  const [updatingStock, setUpdatingStock] = useState(false);
  const [stockMatchBy, setStockMatchBy] = useState("barcode");
  const [stockUpdateStatus, setStockUpdateStatus] = useState(null);
  
  const updateStockOnly = async (matchBy = stockMatchBy) => {
    if (selectedProductKeys.size === 0) {
      toast.error("Lütfen stok güncellenecek ürünleri seçin");
      return;
    }

    setUpdatingStock(true);
    try {
      const selectedProducts = groupedProducts.filter(p => selectedProductKeys.has(p.key));
      const allVariants = [];
      
      for (const product of selectedProducts) {
        for (const variant of product.variants) {
          allVariants.push({
            ProductCode: product.ProductCode,
            Barcode: variant.barcode,
            SKU: variant.sku,
            StockQuantity: variant.stock || 0,
            Price: product.SalesPrice || variant.price || 0,
            SalesPrice: product.SalesPrice || 0,
            SalesPriceEUR: product.SalesPriceEUR || 0,
            SalesPriceUSD: product.SalesPriceUSD || 0,
            SalesPriceGBP: product.SalesPriceGBP || 0,
            ListPrice: product.ListPrice || 0
          });
        }
      }
      
      const matchLabel = matchBy === "sku" ? "SKU" : "Barkod";
      setStockUpdateStatus({ status: "running", total: allVariants.length, completed: 0, updated: 0, not_found: 0, errors: 0, current_product: "Başlatılıyor..." });
      
      const bgResponse = await axios.post(`${API}/nebim/update-stock-background?token=${token}&match_by=${matchBy}`, {
        products: allVariants
      }, { timeout: 30000 });
      
      if (bgResponse.data.success) {
        toast.success(`${allVariants.length} ürün arka planda güncelleniyor (${matchLabel})...`);
        
        const pollInterval = setInterval(async () => {
          try {
            const statusRes = await axios.get(`${API}/nebim/stock-update-status?token=${token}`);
            const s = statusRes.data;
            setStockUpdateStatus(s);
            if (s.status === "completed" || s.status === "idle") {
              clearInterval(pollInterval);
              setUpdatingStock(false);
            }
          } catch (e) { /* devam */ }
        }, 2000);
      } else {
        toast.error(bgResponse.data.message || "Güncelleme başlatılamadı");
        setUpdatingStock(false);
        setStockUpdateStatus(null);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Stok güncelleme hatası");
      setStockUpdateStatus(null);
      setUpdatingStock(false);
    }
  };

  // Miktar bazlı fiyatlandırma modal'ını aç
  const openPricingModal = (product) => {
    setPricingProduct(product);
    // Varolan fiyat kırılımlarını yükle veya varsayılan oluştur
    const existing = productQuantityPricing[product.key] || [];
    if (existing.length === 0) {
      // Varsayılan olarak birim fiyatı ile başla
      setQuantityPricing([{ quantity: 1, totalPrice: product.SalesPrice || 0 }]);
    } else {
      setQuantityPricing([...existing]);
    }
    setPricingModalOpen(true);
  };

  // Fiyat kırılımı ekle
  const addPriceTier = () => {
    const lastQty = quantityPricing.length > 0 ? quantityPricing[quantityPricing.length - 1].quantity : 0;
    const lastPrice = quantityPricing.length > 0 ? quantityPricing[quantityPricing.length - 1].totalPrice : (pricingProduct?.SalesPrice || 0);
    setQuantityPricing([...quantityPricing, { 
      quantity: lastQty + 5, 
      totalPrice: lastPrice * (lastQty + 5) * 0.9 // %10 indirimli varsayılan
    }]);
  };

  // Fiyat kırılımı kaldır
  const removePriceTier = (index) => {
    setQuantityPricing(quantityPricing.filter((_, i) => i !== index));
  };

  // Fiyat kırılımını güncelle
  const updatePriceTier = (index, field, value) => {
    const newPricing = [...quantityPricing];
    newPricing[index] = { ...newPricing[index], [field]: field === 'quantity' ? parseInt(value) || 1 : parseFloat(value) || 0 };
    setQuantityPricing(newPricing);
  };

  // Fiyatlandırmayı kaydet
  const savePricing = () => {
    if (!pricingProduct) return;
    setProductQuantityPricing(prev => ({
      ...prev,
      [pricingProduct.key]: [...quantityPricing].sort((a, b) => a.quantity - b.quantity)
    }));
    toast.success(`${pricingProduct.ProductCode} için fiyat kırılımları kaydedildi`);
    setPricingModalOpen(false);
  };

  // Sayfa ilk açıldığında otomatik yükleme yapma
  // Kullanıcı "Yenile" butonuna basınca yüklenecek
  // useEffect(() => {
  //   fetchNebimProducts();
  // }, [token]);

  // Proxy URL
  const getProxyUrl = (url) => {
    if (!url) return null;
    if (url.startsWith("http://")) {
      return `${API}/proxy/image?url=${encodeURIComponent(url)}`;
    }
    return url;
  };

  return (
    <div className="space-y-6 fade-in" data-testid="products-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Ürünler</h1>
          <p className="text-slate-500 text-sm">Nebim ürünlerini Shopify'a aktarın</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchNebimProducts} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            Yenile
          </Button>
          <Button 
            onClick={() => updateStockOnly("barcode")} 
            disabled={updatingStock || selectedProductKeys.size === 0}
            variant="outline"
            className="border-blue-500 text-blue-600 hover:bg-blue-50"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${updatingStock ? "animate-spin" : ""}`} />
            Barkoda Göre Stok ({selectedProductKeys.size})
          </Button>
          <Button 
            onClick={() => updateStockOnly("sku")} 
            disabled={updatingStock || selectedProductKeys.size === 0}
            variant="outline"
            className="border-purple-500 text-purple-600 hover:bg-purple-50"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${updatingStock ? "animate-spin" : ""}`} />
            SKU'ya Göre Stok ({selectedProductKeys.size})
          </Button>
          <Button 
            onClick={exportToShopify} 
            disabled={exporting || selectedProductKeys.size === 0}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            <Upload className={`w-4 h-4 mr-2 ${exporting ? "animate-pulse" : ""}`} />
            Shopify'a Aktar ({selectedProductKeys.size})
          </Button>
        </div>
      </div>

      {/* Sync Parametreleri */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Calendar className="w-4 h-4" /> Başlangıç Tarihi
              </Label>
              <Input
                type="date"
                value={syncDate}
                onChange={(e) => setSyncDate(e.target.value)}
                className="w-40"
              />
            </div>
            <div className="space-y-2 flex-1">
              <Label>Ürün Kodları (virgülle ayırın, boş bırakırsanız tümü)</Label>
              <Input
                placeholder="MS00182, MS00183, ..."
                value={itemCodes}
                onChange={(e) => setItemCodes(e.target.value)}
              />
            </div>
            <Button onClick={syncProducts} disabled={syncing}>
              <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? "animate-spin" : ""}`} />
              {syncing ? "Senkronize ediliyor..." : "Ürünleri Senkronize Et"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Arama ve Özet */}
      <div className="flex items-center justify-between gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
          <Input
            placeholder="Ürün kodu, adı veya renk ara..."
            value={searchTerm}
            onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
            className="pl-10"
          />
        </div>
        <div className="flex items-center gap-4 text-sm text-slate-500">
          <span><Package className="w-4 h-4 inline mr-1" /> {groupedProducts.length} ürün</span>
          <span><Store className="w-4 h-4 inline mr-1" /> {nebimProducts.length} varyant</span>
        </div>
      </div>

      {/* Stok Güncelleme İlerleme Paneli */}
      {stockUpdateStatus && (stockUpdateStatus.status === "running" || stockUpdateStatus.status === "completed") && (
        <Card className={stockUpdateStatus.status === "completed" ? "border-emerald-300 bg-emerald-50/50" : "border-blue-300 bg-blue-50/50"}>
          <CardContent className="py-4">
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {stockUpdateStatus.status === "running" ? (
                    <RefreshCw className="w-5 h-5 text-blue-600 animate-spin" />
                  ) : (
                    <CheckCircle className="w-5 h-5 text-emerald-600" />
                  )}
                  <span className="font-semibold text-sm">
                    {stockUpdateStatus.status === "running" ? "Stok/Fiyat Güncelleniyor..." : "Güncelleme Tamamlandı"}
                  </span>
                </div>
                <div className="flex items-center gap-4 text-sm">
                  <span className="text-emerald-700 font-medium">{stockUpdateStatus.updated} Güncellendi</span>
                  {stockUpdateStatus.not_found > 0 && (
                    <span className="text-amber-600 font-medium">{stockUpdateStatus.not_found} Bulunamadı</span>
                  )}
                  {stockUpdateStatus.errors > 0 && (
                    <span className="text-red-600 font-medium">{stockUpdateStatus.errors} Hata</span>
                  )}
                  <span className="text-slate-500">{stockUpdateStatus.completed}/{stockUpdateStatus.total}</span>
                  {stockUpdateStatus.status === "completed" && (
                    <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={() => setStockUpdateStatus(null)}>Kapat</Button>
                  )}
                </div>
              </div>
              
              <div className="w-full bg-slate-200 rounded-full h-2.5">
                <div 
                  className={`h-2.5 rounded-full transition-all duration-500 ${stockUpdateStatus.status === "completed" ? "bg-emerald-500" : "bg-blue-500"}`}
                  style={{ width: `${stockUpdateStatus.total > 0 ? Math.round((stockUpdateStatus.completed / stockUpdateStatus.total) * 100) : 0}%` }}
                />
              </div>
              
              {stockUpdateStatus.status === "running" && stockUpdateStatus.current_product && (
                <p className="text-xs text-slate-500 truncate">
                  Güncelleniyor: <span className="text-slate-700">{stockUpdateStatus.current_product}</span>
                </p>
              )}
              
              {/* Mağaza sonuçları */}
              {stockUpdateStatus.status === "completed" && stockUpdateStatus.store_results && stockUpdateStatus.store_results.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-1">
                  {stockUpdateStatus.store_results.map((sr, i) => (
                    <span key={i} className="text-xs bg-white border rounded px-2 py-1">
                      <span className="font-medium">{sr.store}</span> ({sr.currency}): <span className="text-emerald-600">{sr.updated}</span> / <span className="text-amber-500">{sr.not_found} bulunamadı</span>
                    </span>
                  ))}
                </div>
              )}
              
              {/* Hatalı ürünler */}
              {stockUpdateStatus.status === "completed" && stockUpdateStatus.error_list && stockUpdateStatus.error_list.length > 0 && (
                <div className="mt-2 border border-red-200 rounded-lg bg-white p-3 max-h-40 overflow-y-auto">
                  <p className="text-xs font-semibold text-red-600 mb-2">Hatalı Ürünler ({stockUpdateStatus.error_list.length}):</p>
                  {stockUpdateStatus.error_list.map((e, i) => (
                    <div key={i} className="text-xs py-0.5 border-b border-red-50 last:border-0">
                      <span className="font-medium">{e.product}</span> <span className="text-slate-400">({e.store})</span>: <span className="text-red-500">{e.error}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Ürün Tablosu */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50">
                <tr>
                  <th className="p-3 w-10">
                    <Checkbox
                      checked={isAllSelected}
                      onCheckedChange={handleSelectAll}
                    />
                  </th>
                  <th className="text-left p-3 text-sm font-medium text-slate-600 w-20">Resim</th>
                  <th className="text-left p-3 text-sm font-medium text-slate-600">Ürün Kodu</th>
                  <th className="text-left p-3 text-sm font-medium text-slate-600">Ürün Adı</th>
                  <th className="text-left p-3 text-sm font-medium text-slate-600">Renk</th>
                  <th className="text-left p-3 text-sm font-medium text-slate-600">Bedenler</th>
                  <th className="text-right p-3 text-sm font-medium text-slate-600">Fiyat</th>
                  <th className="text-right p-3 text-sm font-medium text-slate-600">Stok</th>
                  <th className="text-center p-3 text-sm font-medium text-slate-600">Miktar Fiyat</th>
                </tr>
              </thead>
              <tbody>
                {paginatedProducts.map((product) => (
                  <tr key={product.key} className="border-t hover:bg-slate-50">
                    <td className="p-3">
                      <Checkbox
                        checked={selectedProductKeys.has(product.key)}
                        onCheckedChange={(checked) => handleSelectProduct(product.key, checked)}
                      />
                    </td>
                    <td className="p-3">
                      <button
                        onClick={() => openPhotoModal(product)}
                        className="w-14 h-14 bg-slate-100 rounded-lg overflow-hidden flex items-center justify-center hover:ring-2 hover:ring-blue-500 transition-all"
                      >
                        <Image className="w-6 h-6 text-slate-400" />
                      </button>
                    </td>
                    <td className="p-3 font-mono text-sm">{product.ProductCode}</td>
                    <td className="p-3">
                      <div className="font-medium">{product.ProductName}</div>
                      {product.Brand && <div className="text-xs text-slate-500">{product.Brand}</div>}
                    </td>
                    <td className="p-3">
                      <Badge variant="secondary">{product.ColorDescription || product.ColorCode || "-"}</Badge>
                    </td>
                    <td className="p-3">
                      <div className="flex flex-wrap gap-1">
                        {product.variants.slice(0, 5).map((v, i) => (
                          <span key={i} className="text-xs bg-slate-100 px-2 py-1 rounded">
                            {v.size}: {v.stock}
                          </span>
                        ))}
                        {product.variants.length > 5 && (
                          <span className="text-xs text-slate-500">+{product.variants.length - 5}</span>
                        )}
                      </div>
                    </td>
                    <td className="p-3 text-right font-medium">
                      {fmtPrice(product.SalesPrice, product.CurrencyCode)}
                    </td>
                    <td className="p-3 text-right">
                      <span className={product.totalStock > 0 ? "text-emerald-600 font-medium" : "text-slate-400"}>
                        {product.totalStock}
                      </span>
                    </td>
                    <td className="p-3 text-center">
                      {productQuantityPricing[product.key]?.length > 0 ? (
                        <div className="flex flex-col items-center gap-1">
                          <div className="flex flex-wrap justify-center gap-1">
                            {productQuantityPricing[product.key].map((tier, i) => (
                              <span key={i} className="text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded">
                                {tier.quantity} ad: {fmtPrice(tier.totalPrice / tier.quantity, product.CurrencyCode)}
                              </span>
                            ))}
                          </div>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 text-xs text-purple-600 hover:text-purple-800 px-2"
                            onClick={() => openPricingModal(product)}
                            data-testid={`pricing-btn-${product.key}`}
                          >
                            Düzenle
                          </Button>
                        </div>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-8"
                          onClick={() => openPricingModal(product)}
                          data-testid={`pricing-btn-${product.key}`}
                        >
                          <DollarSign className="w-4 h-4 mr-1" />
                          Ekle
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Sayfalama */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between p-4 border-t">
              <span className="text-sm text-slate-500">
                {filteredProducts.length} üründen {(currentPage - 1) * itemsPerPage + 1}-{Math.min(currentPage * itemsPerPage, filteredProducts.length)} gösteriliyor
              </span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  Önceki
                </Button>
                <span className="px-3 py-1 text-sm">{currentPage} / {totalPages}</span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                >
                  Sonraki
                </Button>
              </div>
            </div>
          )}

          {paginatedProducts.length === 0 && !loading && (
            <div className="text-center py-12 text-slate-500">
              {searchTerm ? "Arama kriterine uygun ürün bulunamadı" : "Ürün bulunamadı. Önce senkronize edin."}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Fotoğraf Modal */}
      <Dialog open={photoModalOpen} onOpenChange={setPhotoModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between">
              <div>
                <span className="font-bold">{photoModalProduct?.ProductCode}</span>
                <span className="text-slate-500 font-normal ml-2">
                  Renk: {photoModalProduct?.ColorDescription} ({photoModalProduct?.ColorCode})
                </span>
              </div>
            </DialogTitle>
          </DialogHeader>

          {loadingPhotos ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="w-8 h-8 animate-spin text-slate-400" />
            </div>
          ) : productPhotos.length > 0 ? (
            <div className="space-y-4">
              {/* Ana Fotoğraf */}
              <div className="relative bg-slate-100 rounded-lg overflow-hidden" style={{ height: "400px" }}>
                <img
                  src={getProxyUrl(productPhotos[currentPhotoIndex]?.Photo)}
                  alt={`Fotoğraf ${currentPhotoIndex + 1}`}
                  className="w-full h-full object-contain"
                />
                
                {/* Navigasyon okları */}
                {productPhotos.length > 1 && (
                  <>
                    <button
                      onClick={() => setCurrentPhotoIndex(i => i > 0 ? i - 1 : productPhotos.length - 1)}
                      className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-2 shadow"
                    >
                      <ChevronLeft className="w-6 h-6" />
                    </button>
                    <button
                      onClick={() => setCurrentPhotoIndex(i => i < productPhotos.length - 1 ? i + 1 : 0)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-2 shadow"
                    >
                      <ChevronRight className="w-6 h-6" />
                    </button>
                  </>
                )}

                {/* Fotoğraf numarası */}
                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 bg-black/60 text-white px-3 py-1 rounded-full text-sm">
                  {currentPhotoIndex === 0 ? "Ana Foto" : `${currentPhotoIndex + 1}. Ürün`} / {productPhotos.length}
                </div>
              </div>

              {/* Thumbnail'ler */}
              {productPhotos.length > 1 && (
                <div className="flex gap-2 overflow-x-auto pb-2">
                  {productPhotos.map((photo, idx) => (
                    <button
                      key={idx}
                      onClick={() => setCurrentPhotoIndex(idx)}
                      className={`flex-shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                        currentPhotoIndex === idx ? "border-blue-500" : "border-transparent"
                      }`}
                    >
                      <img
                        src={getProxyUrl(photo.Photo)}
                        alt={`Thumbnail ${idx + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-slate-400">
              <Image className="w-16 h-16 mb-2" />
              <span>Fotoğraf bulunamadı</span>
            </div>
          )}

          <Button onClick={() => setPhotoModalOpen(false)} className="w-full">
            Kapat
          </Button>
        </DialogContent>
      </Dialog>

      {/* Miktar Bazlı Fiyatlandırma Modal */}
      <Dialog open={pricingModalOpen} onOpenChange={setPricingModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-purple-600" />
              Miktar Bazlı Fiyatlandırma
            </DialogTitle>
            <DialogDescription>
              {pricingProduct?.ProductCode} - {pricingProduct?.ColorDescription} için farklı miktarlarda farklı fiyatlar belirleyin
            </DialogDescription>
          </DialogHeader>

          <div className="py-4 space-y-4">
            {/* Mevcut birim fiyat bilgisi */}
            <div className="bg-slate-50 rounded-lg p-3 space-y-1">
              <div className="flex justify-between items-center">
                <span className="text-sm text-slate-600">TRY:</span>
                <span className="font-mono font-medium">{fmtPrice(pricingProduct?.SalesPrice, "TRY")}</span>
              </div>
              {pricingProduct?.SalesPriceUSD > 0 && (
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-600">USD:</span>
                  <span className="font-mono font-medium text-blue-700">${pricingProduct.SalesPriceUSD?.toLocaleString()}</span>
                </div>
              )}
              {pricingProduct?.SalesPriceEUR > 0 && (
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-600">EUR:</span>
                  <span className="font-mono font-medium text-emerald-700">€{pricingProduct.SalesPriceEUR?.toLocaleString()}</span>
                </div>
              )}
              {pricingProduct?.SalesPriceGBP > 0 && (
                <div className="flex justify-between items-center">
                  <span className="text-sm text-slate-600">GBP:</span>
                  <span className="font-mono font-medium text-purple-700">£{pricingProduct.SalesPriceGBP?.toLocaleString()}</span>
                </div>
              )}
            </div>

            {/* Fiyat Kırılımları */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-medium">Fiyat Kırılımları</Label>
                <Button size="sm" variant="outline" onClick={addPriceTier} data-testid="add-price-tier-btn">
                  <Plus className="w-4 h-4 mr-1" />
                  Kırılım Ekle
                </Button>
              </div>

              {quantityPricing.length === 0 ? (
                <div className="text-center py-6 text-slate-400 border rounded-lg border-dashed">
                  <DollarSign className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Henüz fiyat kırılımı eklenmedi</p>
                  <p className="text-xs mt-1">Shopify'a tek fiyatla aktarılacak</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {quantityPricing.map((tier, idx) => (
                    <div key={idx} className="flex items-center gap-3 bg-slate-50 rounded-lg p-3">
                      <div className="flex-1">
                        <Label className="text-xs text-slate-500">Adet</Label>
                        <Input
                          type="number"
                          min="1"
                          value={tier.quantity}
                          onChange={(e) => updatePriceTier(idx, 'quantity', e.target.value)}
                          className="h-9 mt-1"
                          data-testid={`qty-input-${idx}`}
                        />
                      </div>
                      <div className="flex-1">
                        <Label className="text-xs text-slate-500">Toplam Fiyat</Label>
                        <Input
                          type="number"
                          min="0"
                          step="0.01"
                          value={tier.totalPrice}
                          onChange={(e) => updatePriceTier(idx, 'totalPrice', e.target.value)}
                          className="h-9 mt-1"
                          data-testid={`price-input-${idx}`}
                        />
                      </div>
                      <div className="flex-1">
                        <Label className="text-xs text-slate-500">Birim Fiyat</Label>
                        <div className="h-9 mt-1 flex items-center font-mono text-emerald-600 text-sm">
                          {tier.quantity > 0 ? (tier.totalPrice / tier.quantity).toFixed(2) : "0.00"} {CURRENCY_MAP[(pricingProduct?.CurrencyCode || "TRY").toUpperCase()] || "₺"}/ad
                        </div>
                      </div>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-9 w-9 text-red-500 hover:text-red-700 hover:bg-red-50 mt-5"
                        onClick={() => removePriceTier(idx)}
                        data-testid={`remove-tier-${idx}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Bilgi notu */}
            {quantityPricing.length > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-700">
                <strong>Not:</strong> Her fiyat kırılımı Shopify'da ayrı bir varyant olarak oluşturulacaktır.
                Örn: "1 Adet - 100₺", "5 Adet - 450₺" şeklinde.
              </div>
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setPricingModalOpen(false)}>
              İptal
            </Button>
            <Button onClick={savePricing} className="bg-purple-600 hover:bg-purple-700" data-testid="save-pricing-btn">
              <DollarSign className="w-4 h-4 mr-2" />
              Kaydet
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
